import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";
import { Trash, Warning } from '@phosphor-icons/react';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  itemName?: string;
  itemDetails?: string[];
  isLoading?: boolean;
  confirmText?: string;
  cancelText?: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  itemName,
  itemDetails = [],
  isLoading = false,
  confirmText = "Verwijderen",
  cancelText = "Annuleren"
}) => {
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="md"
      classNames={{
        backdrop: "bg-neutral-primary/50 backdrop-blur-xs",
        base: "border border-neutral-primary/20",
        header: "border-b border-neutral-primary/20",
        footer: "border-t border-neutral-primary/20",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-3 text-neutral-primary">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brick-primary/10">
            <Warning size={24} className="text-brick-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
        </ModalHeader>
        
        <ModalBody className="py-6">
          <div className="space-y-4">
            <p className="text-neutral-primary">
              {description}
            </p>
            
            {itemName && (
              <div className="bg-brick-secondary-fine rounded-lg p-4 border border-neutral-primary/10">
                <div className="flex items-center gap-3 mb-2">
                  <Trash size={20} className="text-brick-primary" />
                  <span className="font-medium text-neutral-primary">Te verwijderen item:</span>
                </div>
                <p className="text-neutral-primary font-semibold ml-8">{itemName}</p>
                
                {itemDetails.length > 0 && (
                  <div className="mt-3 ml-8 space-y-1">
                    {itemDetails.map((detail, index) => (
                      <p key={index} className="text-sm text-neutral-secondary-mid">
                        {detail}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Warning size={20} className="text-yellow-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Let op: Deze actie kan niet ongedaan worden gemaakt
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Alle gegevens gerelateerd aan dit item worden permanent verwijderd.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        
        <ModalFooter className="gap-3">
          <Button
            variant="light"
            onPress={onClose}
            disabled={isLoading}
            className="text-neutral-primary hover:bg-neutral-primary/10"
          >
            {cancelText}
          </Button>
          <Button
            color="danger"
            onPress={onConfirm}
            isLoading={isLoading}
            startContent={!isLoading ? <Trash size={16} /> : undefined}
            className="bg-brick-primary text-white hover:bg-brick-primary/90"
          >
            {confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteConfirmationModal; 